import type { DeviceConfigurationApi } from '@model/DeviceConfiguration';
import type {
  BodyImageComparisonMetric,
  BodyImageMeasurementSnapshot,
} from '@types';
import type {
  BiologicalSex,
  PatientMeasurementRecord,
} from '../patients/types';
import { getBodyImageFigureByStep, type BodyImageFigureSex } from './figureMap';

export {
  BODY_IMAGE_FEMALE_FIGURES,
  BODY_IMAGE_FIGURE_MAP,
  BODY_IMAGE_FIGURE_STEPS,
  BODY_IMAGE_MALE_FIGURES,
  getBodyImageFigureByStep,
  getBodyImageFiguresForSex,
} from './figureMap';
export type {
  BodyImageFigureMeasurements,
  BodyImageFigureSex,
  BodyImageFigureStep,
  BodyImageNormalizedMetricKey,
} from './figureMap';

export const BODY_IMAGE_SELECTED_FIGURE_CONFIG_KEY = 'selectedFigure' as const;

/** Ably message names used by Body Image (hub→device + echo filter). */
export const isBodyImageAblyEventName = (
  eventName: string | null | undefined
): boolean => {
  if (eventName == null || eventName === '') return false;
  return eventName === 'start_bodyimage' || eventName.startsWith('bodyImage:');
};

const toBodyImageConfigRecord = (config: unknown): Record<string, unknown> => {
  if (config != null && typeof config === 'object' && !Array.isArray(config)) {
    return { ...(config as Record<string, unknown>) };
  }

  if (typeof config === 'string' && config.trim() !== '') {
    try {
      const parsed = JSON.parse(config) as unknown;
      if (
        parsed != null &&
        typeof parsed === 'object' &&
        !Array.isArray(parsed)
      ) {
        return { ...(parsed as Record<string, unknown>) };
      }
    } catch {
      return { mode: 'body-image' };
    }
  }

  return { mode: 'body-image' };
};

/** Legge la figura selezionata (1–9) salvata in `session.config`. */
export const getBodyImageSelectedFigure = (config: unknown): number | null => {
  const record = toBodyImageConfigRecord(config);
  const figure = Number(record[BODY_IMAGE_SELECTED_FIGURE_CONFIG_KEY]);

  if (!Number.isInteger(figure) || figure < 1 || figure > 9) {
    return null;
  }

  return figure;
};

/** Merge di `selectedFigure` nel config body-image da persistere in DB. */
export const withBodyImageSelectedFigure = (
  config: unknown,
  selectedFigure: number | null
): Record<string, unknown> => {
  const next = toBodyImageConfigRecord(config);

  if (next.mode == null) {
    next.mode = 'body-image';
  }

  if (selectedFigure == null) {
    delete next[BODY_IMAGE_SELECTED_FIGURE_CONFIG_KEY];
  } else {
    next[BODY_IMAGE_SELECTED_FIGURE_CONFIG_KEY] = selectedFigure;
  }

  return next;
};
type ComparableMetricDefinition = {
  key: keyof BodyImageMeasurementSnapshot | string;
  label: string;
  aliases: string[];
  metricTypes: string[];
};

export const BODY_IMAGE_METRIC_DEFINITIONS: ComparableMetricDefinition[] = [
  {
    key: 'shoulders',
    label: 'Spalle',
    aliases: ['spalle', 'spalla', 'shoulder', 'shoulders'],
    metricTypes: ['shoulder_cm'],
  },
  {
    key: 'hips',
    label: 'Fianchi',
    aliases: ['fianchi', 'hip', 'hips'],
    metricTypes: ['hips_cm'],
  },
  {
    key: 'waist',
    label: 'Vita',
    aliases: ['vita', 'waist'],
    metricTypes: ['waist_cm'],
  },
  {
    key: 'bust',
    label: 'Petto',
    aliases: ['petto', 'chest', 'bust'],
    metricTypes: ['chest_cm'],
  },
  {
    key: 'neck',
    label: 'Collo',
    aliases: ['collo', 'neck'],
    metricTypes: ['neck_cm'],
  },
  {
    key: 'thigh',
    label: 'Coscia',
    aliases: ['coscia', 'thigh'],
    metricTypes: ['thigh_cm'],
  },
  {
    key: 'calf',
    label: 'Polpacci',
    aliases: ['polpaccio', 'polpacci', 'calf', 'calves'],
    metricTypes: ['calf_cm'],
  },
  {
    key: 'weight',
    label: 'Peso',
    aliases: ['peso', 'weight'],
    metricTypes: ['weight_kg'],
  },
];

const normalizeKey = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

const toNumber = (value: string): number | null => {
  const normalized = value.replace(',', '.').trim();
  if (normalized === '') return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

export const extractBodyImageMeasurementSnapshot = (
  measurement: PatientMeasurementRecord | null
): BodyImageMeasurementSnapshot => {
  if (measurement == null) {
    return {};
  }

  const snapshot: BodyImageMeasurementSnapshot = {};

  for (const field of measurement.fields) {
    const numericValue = toNumber(field.value);
    if (numericValue == null) continue;

    const normalizedLabel = normalizeKey(field.label);
    const match = BODY_IMAGE_METRIC_DEFINITIONS.find(definition => {
      if (
        field.metricType != null &&
        definition.metricTypes.includes(field.metricType)
      ) {
        return true;
      }

      return definition.aliases.some(alias => normalizedLabel.includes(alias));
    });

    if (match) {
      snapshot[match.key] = numericValue;
    }
  }

  return snapshot;
};

export const resolveBodyImageBiologicalSex = (
  biologicalSex: BiologicalSex = null,
  measurement: PatientMeasurementRecord | null = null
): BiologicalSex => {
  if (biologicalSex === 'male' || biologicalSex === 'female') {
    return biologicalSex;
  }

  const fromMeasurement = measurement?.biologicalSex;
  if (fromMeasurement === 'male' || fromMeasurement === 'female') {
    return fromMeasurement;
  }

  return null;
};

export const buildBodyImageStartPayload = (
  session: DeviceConfigurationApi,
  measurement: PatientMeasurementRecord | null,
  biologicalSex: BiologicalSex = null
) => {
  const gender = resolveBodyImageBiologicalSex(biologicalSex, measurement);
  const measurements = extractBodyImageMeasurementSnapshot(measurement);
  const patientPayload = {
    gender,
    ...measurements,
  } as Record<string, unknown>;

  const legacyPatientFields = {
    ...(measurements.bust != null ? { patient_chest: measurements.bust } : {}),
    ...(measurements.waist != null
      ? { patient_waist: measurements.waist }
      : {}),
    ...(measurements.hips != null ? { patient_hips: measurements.hips } : {}),
    ...(measurements.shoulders != null
      ? { patient_shoulders: measurements.shoulders }
      : {}),
  };

  const payload = {
    code: session.deviceCode,
    command: 'start_bodyimage',
    patient_gender: gender,
    ...legacyPatientFields,
    map: 'bodyimage',
    sessionId: String(session.id),
    patientId: session.patient_id,
    timestamp: new Date().toISOString(),
    patient: patientPayload,
  } as Record<string, unknown>;

  return payload;
};

const BODY_IMAGE_FIGURES = [1, 2, 3, 4, 5, 6, 7, 8, 9] as const;

const buildBodyImageEventEnvelope = (
  session: DeviceConfigurationApi,
  eventName: string
) => ({
  code: session.deviceCode,
  command: eventName,
  sessionId: String(session.id),
  patientId: session.patient_id,
  timestamp: new Date().toISOString(),
});

export const buildBodyImageMannequinsHomePayload = (
  session: DeviceConfigurationApi
) => ({
  ...buildBodyImageEventEnvelope(session, 'bodyImage:mannequins:home'),
  figures: [...BODY_IMAGE_FIGURES],
});

export const BODY_IMAGE_MANNEQUIN_EVENT_NAME = 'bodyImage:mannequin' as const;

export const buildBodyImageMannequinSelectPayload = (
  session: DeviceConfigurationApi,
  figure: number
) => ({
  ...buildBodyImageEventEnvelope(session, BODY_IMAGE_MANNEQUIN_EVENT_NAME),
  value: figure,
});

export const BODY_IMAGE_ROTATE_EVENT_NAME = 'bodyImage:rotate' as const;

export const buildBodyImageMannequinsRotatePayload = (
  session: DeviceConfigurationApi,
  signedDegrees: number
) => {
  const value = Math.max(-360, Math.min(360, signedDegrees));

  return {
    ...buildBodyImageEventEnvelope(session, BODY_IMAGE_ROTATE_EVENT_NAME),
    value,
  };
};

export type BodyImageComparePresetSlot = {
  type: 'preset';
  value: number;
};

export type BodyImageCompareCustomSlot = {
  type: 'custom';
  comparisonId: number;
  label: string | null;
  parameters: Record<string, unknown>;
};

export type BodyImageCompareFigureSlot =
  | BodyImageComparePresetSlot
  | BodyImageCompareCustomSlot;

/** Misure comparison inviate ad Ably così come salvate (cm/kg), senza normalizzazione morph. */
export const extractBodyImageParametersForAbly = (
  parameters: Record<string, unknown>
): Record<string, number> =>
  Object.fromEntries(
    Object.entries(parameters)
      .map(([key, value]) => {
        const numericValue =
          typeof value === 'number'
            ? value
            : typeof value === 'string'
              ? Number(value.replace(',', '.').trim())
              : NaN;

        if (!Number.isFinite(numericValue)) {
          return null;
        }

        return [key, numericValue] as const;
      })
      .filter((entry): entry is readonly [string, number] => entry != null)
  );

export const buildBodyImageMannequinsComparePayload = (
  session: DeviceConfigurationApi,
  selectedFigure: number,
  comparison: {
    id: number;
    label: string | null;
    parameters: Record<string, unknown>;
  }
) => {
  const parameters = extractBodyImageParametersForAbly(comparison.parameters);

  return {
    ...buildBodyImageEventEnvelope(session, 'bodyImage:mannequins:compare'),
    figures: [
      { type: 'preset' as const, value: selectedFigure },
      {
        type: 'custom' as const,
        comparisonId: comparison.id,
        label: comparison.label,
        parameters,
      },
    ] satisfies BodyImageCompareFigureSlot[],
  };
};

const toBodyImageFigureSex = (
  biologicalSex: BiologicalSex
): BodyImageFigureSex => (biologicalSex === 'male' ? 'male' : 'female');

/** Misure hardcoded della figura N (1–9) per sesso. */
export const getBodyImageMannequinMeasurements = (
  figure: number,
  biologicalSex: BiologicalSex = null
) => getBodyImageFigureByStep(toBodyImageFigureSex(biologicalSex), figure);

export const calculateBodyImageComparisonMetrics = (
  baselineMeasurements: BodyImageMeasurementSnapshot,
  comparisonMeasurements: BodyImageMeasurementSnapshot
): Array<
  Pick<
    BodyImageComparisonMetric,
    'metricKey' | 'patientValue' | 'comparisonValue' | 'differencePercent'
  >
> =>
  BODY_IMAGE_METRIC_DEFINITIONS.map(definition => {
    const patientValue = baselineMeasurements[definition.key] ?? null;
    const comparisonValue = comparisonMeasurements[definition.key] ?? null;

    let differencePercent: number | null = null;
    if (
      patientValue != null &&
      comparisonValue != null &&
      Number.isFinite(patientValue) &&
      patientValue !== 0
    ) {
      differencePercent = Number(
        (((comparisonValue - patientValue) / patientValue) * 100).toFixed(2)
      );
    }

    return {
      metricKey: definition.key,
      patientValue,
      comparisonValue,
      differencePercent,
    };
  }).filter(
    metric => metric.patientValue != null || metric.comparisonValue != null
  );

export const formatBodyImageMetricLabel = (metricKey: string) =>
  BODY_IMAGE_METRIC_DEFINITIONS.find(definition => definition.key === metricKey)
    ?.label ?? metricKey;

export const formatBodyImageMetricValue = (
  metricKey: string,
  value: unknown
): string => {
  const numericValue =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
        ? Number(value.replace(',', '.'))
        : Number.NaN;

  if (!Number.isFinite(numericValue)) {
    return '—';
  }

  if (metricKey === 'weight') {
    return `${numericValue.toFixed(1)} kg`;
  }

  return `${numericValue.toFixed(1)} cm`;
};

export const getBodyImageComparisonMetricEntries = (
  parameters: Record<string, unknown>
) =>
  BODY_IMAGE_METRIC_DEFINITIONS.map(definition => ({
    definition,
    value: parameters[definition.key],
  })).filter(({ value }) => {
    if (value == null || value === '') {
      return false;
    }

    const numericValue =
      typeof value === 'number'
        ? value
        : Number(String(value).replace(',', '.'));
    return Number.isFinite(numericValue);
  });

export const extractBodyImageMeasurementsFromTrackingPayload = (
  payload: Record<string, unknown>
): BodyImageMeasurementSnapshot => {
  const patient =
    payload.patient != null &&
    typeof payload.patient === 'object' &&
    !Array.isArray(payload.patient)
      ? (payload.patient as Record<string, unknown>)
      : null;

  const measurements =
    patient != null
      ? Object.fromEntries(
          Object.entries(patient).filter(([key]) => key !== 'gender')
        )
      : payload.measurements;

  if (
    measurements == null ||
    typeof measurements !== 'object' ||
    Array.isArray(measurements)
  ) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(measurements)
      .map(([key, value]) => {
        if (typeof value === 'number' && Number.isFinite(value)) {
          return [key, value];
        }
        if (typeof value === 'string') {
          const parsed = Number(value);
          if (Number.isFinite(parsed)) {
            return [key, parsed];
          }
        }
        return [key, null];
      })
      .filter((entry): entry is [string, number] => entry[1] != null)
  );
};

export const findPatientMeasurementAtOrBefore = (
  history: PatientMeasurementRecord[],
  instant: string | null | undefined
): PatientMeasurementRecord | null => {
  if (history.length === 0) {
    return null;
  }

  if (instant == null || instant.trim() === '') {
    return history[0];
  }

  const targetTime = new Date(instant).getTime();
  if (Number.isNaN(targetTime)) {
    return history[0];
  }

  const sorted = [...history].sort(
    (left, right) =>
      new Date(right.createdAt ?? right.updatedAt ?? 0).getTime() -
      new Date(left.createdAt ?? left.updatedAt ?? 0).getTime()
  );

  return (
    sorted.find(measurement => {
      const measurementTime = new Date(
        measurement.createdAt ?? measurement.updatedAt ?? 0
      ).getTime();
      return !Number.isNaN(measurementTime) && measurementTime <= targetTime;
    }) ??
    sorted[sorted.length - 1] ??
    null
  );
};

export const resolveBodyImageSessionBaselineMeasurements = ({
  startEventPayload,
  measurementRecord,
}: {
  startEventPayload: Record<string, unknown> | null;
  measurementRecord: PatientMeasurementRecord | null;
}): BodyImageMeasurementSnapshot => {
  const fromStartEvent =
    startEventPayload == null
      ? {}
      : extractBodyImageMeasurementsFromTrackingPayload(startEventPayload);

  if (Object.keys(fromStartEvent).length > 0) {
    return fromStartEvent;
  }

  return extractBodyImageMeasurementSnapshot(measurementRecord);
};

export const getBodyImageFigurePlaceholderSrc = (figure: number) => {
  const bodyWidth = 28 + (figure - 1) * 4;
  const hipWidth = bodyWidth + 8;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120" fill="none">
      <rect width="120" height="120" fill="#F1F3F5"/>
      <g transform="translate(0 6) scale(1 0.72)">
        <circle cx="60" cy="28" r="14" fill="#ADB5BD"/>
        <path d="M${60 - bodyWidth / 2} 48
          C ${60 - bodyWidth / 2} 72, ${60 - hipWidth / 2} 96, ${60 - hipWidth / 2} 118
          L ${60 - 10} 118 L ${60 - 10} 148 L ${60 + 10} 148 L ${60 + 10} 118
          L ${60 + hipWidth / 2} 118
          C ${60 + hipWidth / 2} 96, ${60 + bodyWidth / 2} 72, ${60 + bodyWidth / 2} 48
          Z" fill="#CED4DA"/>
      </g>
    </svg>
  `.trim();

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

/** Placeholder figure 1–9 stimata dalle misure custom (peso) per la preview UI. */
export const getPlaceholderFigureFromParameters = (
  parameters: Record<string, unknown>
) => {
  const weight = Number(parameters.weight);
  if (!Number.isFinite(weight)) {
    return 5;
  }

  return Math.min(9, Math.max(1, Math.round(((weight - 45) / 70) * 8) + 1));
};
